import { Text, tokens } from "@fluentui/react-components";
import { ChevronRight20Regular } from "@fluentui/react-icons";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApprovalBadge } from "@/components/ApprovalBadge";
import { useDocumentTypes } from "@/features/documents/useDocumentTypes";
import { formatDocumentType } from "@/lib/formatDocumentType";
import { useSelectionStore } from "@/store/selectionStore";
import { isArchiveOnly, type DocumentListItem } from "@/types/domain";

export function DocumentRow({ doc }: { doc: DocumentListItem }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: documentTypes } = useDocumentTypes();
  const selectedEmailId = useSelectionStore((s) => s.selectedEmailId);
  const label = formatDocumentType(doc.documentType, documentTypes);
  const archiveOnly = isArchiveOnly(doc.documentType, documentTypes);

  // Propagate the current list filters (and remember which email we
  // came from) so Back navigation restores the same list view.
  const params = new URLSearchParams(searchParams);
  params.delete("email");
  if (selectedEmailId) params.set("fromEmail", selectedEmailId);
  const target = `/review/${encodeURIComponent(doc.id)}${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(target)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(target);
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        borderRadius: 6,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        background: tokens.colorNeutralBackground1,
        cursor: "pointer",
      }}
    >
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <Text weight="semibold">{label}</Text>
        <Text
          size={200}
          style={{
            color: tokens.colorNeutralForeground2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {doc.attachmentName ?? "Email body"}
          {doc.sourcePages ? ` · Pages ${doc.sourcePages}` : ""}
        </Text>
        {doc.errorMessage ? (
          <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
            {doc.errorMessage}
          </Text>
        ) : null}
      </div>
      <ApprovalBadge status={doc.status} approvalStatus={doc.approvalStatus} archiveOnly={archiveOnly} />
      <ChevronRight20Regular />
    </div>
  );
}
